import { useWeb3React } from '@web3-react/core';
import { useContext, useEffect, useMemo, useState } from 'react';
import errorContext from '../../context/error/errorContext';
import { getChainInfo } from '../../config/constants/chainInfo';
import { getConnectorProvider, toHex } from '../../web3/utils';
import { CHAIN_IDS_TO_NAMES, SupportedChains } from '../../config/constants';
import Select from '../../components/Select';
import { ChainIcons } from '../../assets/icons';
import { FortmaticConnector } from 'web3-react/dist/connectors';
import { PortisConnector } from '@web3-react/portis-connector';
import LedgerHQConnector from '../../web3/LedgerHQSigner/LedgerHQConnector';
import { SafeAppConnector } from '@gnosis.pm/safe-apps-web3-react';
import connectionContext from '../../context/connection/connectionContext';

export type AppParam = {
    network: string;
};

const search = window.location.search;
const params = new URLSearchParams(search);
const network = params.get('network');

const ChainSelector = () => {
    const { library, chainId, connector } = useWeb3React();
    const { setError } = useContext(errorContext);
    const [open, setOpen] = useState(false);
    const [networkSwitched, setNetworkSwitched] = useState(false);
    const { activeConnector } = useContext(connectionContext);

    useEffect(() => {
        if (chainId === SupportedChains.MAINNET) {
            window.history.pushState(null, '', window.location.href.split('?')[0]);
        }
        if (chainId === SupportedChains.ARBITRUM_ONE) {
            window.history.pushState(null, '', '?network=arbitrum');
        }
    }, [chainId]);

    useEffect(() => {
        (async () => {
            try {
                if (!networkSwitched && library && network === 'arbitrum') {
                    await library.provider.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: toHex(SupportedChains.ARBITRUM_ONE) }],
                    });
                    setNetworkSwitched(true);
                    return;
                }
            } catch (error) {
                console.log(error);
            }
        })();
    }, [networkSwitched, library]);

    const connectedChain = useMemo(
        () =>
            Object.entries(CHAIN_IDS_TO_NAMES).filter(
                ([networkId]) => networkId === chainId?.toString()
            )[0],
        [chainId]
    );

    const switchNetwork = async (network: SupportedChains) => {
        if (
            connector &&
            connector instanceof SafeAppConnector &&
            (await connector.isSafeApp())
        ) {
            return;
        }
        if (
            connector instanceof FortmaticConnector ||
            connector instanceof PortisConnector ||
            connector instanceof LedgerHQConnector
        ) {
            setError({ type: 'Unsupported Wallet' });
            return;
        }
        try {
            const provider = getConnectorProvider(activeConnector.name);

            if (!library) {
                await provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: toHex(network) }],
                });
                return;
            }
            await library.provider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: toHex(network) }],
            });
        } catch (switchError: any) {
            if (switchError.code === 4902) {
                try {
                    await library.provider.request({
                        method: 'wallet_addEthereumChain',
                        params: [getChainInfo(network)],
                    });
                    return;
                } catch (error) {
                    setError(error);
                }
            }
            switchError.code = 1001;
            setError(switchError);
        }
    };

    return (
        <>
            {!(connector && connector instanceof SafeAppConnector) && (
                <div className='network-selector'>
                    <Select
                        selected={
                            <>
                                {chainId ? (
                                    <div>
                                        {ChainIcons[chainId]}
                                        <span>{CHAIN_IDS_TO_NAMES[chainId]}</span>
                                    </div>
                                ) : (
                                    <div>
                                        <span>Invalid Chain</span>
                                    </div>
                                )}
                            </>
                        }
                        options={[
                            ...Object.entries(CHAIN_IDS_TO_NAMES).map(
                                ([chainId, chainName]) => ({
                                    chainId,
                                    chainName,
                                })
                            ),
                        ]}
                        activeDropdownKey={
                            connectedChain
                                ? {
                                      chainId: connectedChain[0],
                                      chainName: connectedChain[1],
                                  }
                                : null
                        }
                        open={open}
                        setOpen={() => setOpen((prev) => !prev)}
                        optionKey={'chainId'}
                        onChange={(option) => switchNetwork(option.chainId)}
                        renderOption={(option) => (
                            <div>
                                <div className='chain-icon'>
                                    {ChainIcons[option.chainId]}
                                </div>
                                <span>{option.chainName}</span>
                            </div>
                        )}
                    />
                </div>
            )}
        </>
    );
};

export default ChainSelector;
